import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import InfoModal from '../components/InfoModal';
import {
  getDependencies,
  resolveDependency,
  dismissDependency,
  deleteDependency,
  handleDependencyByAi,
  Dependency,
} from '../services/api';

// ============================================================================
// Dependencies page.
//
// The GENERIC "ask a human for anything" inbox: anything blocked on a specific
// person/role, grouped by who owns it. A dependency can be any kind —
//   question -> multiline answer field + Submit answer (resolution = the answer)
//   task     -> Mark done button + optional note
//   api_key  -> write-only key field + Save (auto-derived from the integrations
//               catalog for every missing key; resolving sets the env var)
//   approval -> Approve / Decline
//   info / access / decision / other -> a resolution text field + Resolve
// Each lists what unblocks it. Stays out of the way when nothing is open.
//
// SECURITY: secret values are NEVER shown — the API returns only metadata
// (for api_key deps, the env var NAME, never the value). The key input is
// write-only.
// ============================================================================

const ROLE_LABEL: Record<string, string> = {
  joshua: 'Joshua — Founder / Ops',
  subham: 'Subham — Product',
  aazmeer: 'Aazmeer — Growth',
  thadeus: 'Thadeus — Impact & Ads',
  zee: 'Zee — Operations / Content',
};

const roleLabel = (role: string): string =>
  ROLE_LABEL[role] || role.charAt(0).toUpperCase() + role.slice(1);

const KIND_LABEL: Record<string, string> = {
  api_key: 'API Key',
  approval: 'Approval',
  info: 'Info',
  access: 'Access',
  decision: 'Decision',
  task: 'Task',
  question: 'Question',
  other: 'Other',
};

const Dependencies: React.FC = () => {
  const { theme } = useTheme();
  const [items, setItems] = useState<Dependency[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await getDependencies(undefined, 'open'));
    } catch (err: any) {
      console.error('Failed to load dependencies', err);
      setError(err.response?.data?.detail || 'Failed to load dependencies.');
      setItems(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    padding: '20px',
    minWidth: 0,
  };

  // Group dependencies by owner_role, preserving order.
  const groups: { role: string; deps: Dependency[] }[] = [];
  if (items) {
    for (const it of items) {
      let g = groups.find((x) => x.role === it.owner_role);
      if (!g) {
        g = { role: it.owner_role, deps: [] };
        groups.push(g);
      }
      g.deps.push(it);
    }
  }

  const openCount = items ? items.length : 0;

  return (
    <div className="container" style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px', overflowX: 'hidden' }}>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h1 style={{ color: theme.text, margin: 0 }}>Dependencies &amp; Integrations</h1>
          <InfoModal
            title="Dependencies & Integrations"
            body={[
              "Everything the system is blocked on, grouped by who owns it — answer a question, do a task, approve something, or provide an API key. Resolve each inline.",
              "Missing integration keys (Integrations) are surfaced here automatically as dependencies. Secret values are write-only and never shown back.",
              "Clearing these unblocks the AI to keep working autonomously.",
            ]}
          />
        </div>
        <p style={{ color: theme.textSecondary, margin: '4px 0 0', fontSize: '14px', maxWidth: '720px' }}>
          Requests that need you — answer a question, do a task, approve something, or provide a key.
          Grouped by who owns each, with a way to resolve it inline. Missing API keys are surfaced
          automatically. Secret values are never shown.
        </p>
        {items && (
          <div style={{ marginTop: '10px' }}>
            {openCount === 0 ? (
              <span style={{ color: theme.success, fontSize: '13px', fontWeight: 600 }}>
                ✓ Nothing blocked — no open dependencies.
              </span>
            ) : (
              <span style={{ color: theme.warning, fontSize: '13px', fontWeight: 600 }}>
                {openCount} open dependenc{openCount === 1 ? 'y' : 'ies'} need attention.
              </span>
            )}
          </div>
        )}
      </div>

      {loading && (
        <div style={{ ...cardStyle, textAlign: 'center', color: theme.textSecondary }}>
          Loading dependencies…
        </div>
      )}

      {!loading && error && (
        <div
          style={{
            padding: '16px',
            backgroundColor: '#ef444422',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            color: '#ef4444',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && items && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {groups.map((group) => (
            <div key={group.role} style={cardStyle}>
              <h2 style={{ color: theme.text, margin: '0 0 4px', fontSize: '18px' }}>
                {roleLabel(group.role)}
              </h2>
              <p style={{ color: theme.textSecondary, margin: '0 0 16px', fontSize: '13px' }}>
                {group.deps.length} open dependenc{group.deps.length === 1 ? 'y' : 'ies'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {group.deps.map((d) => (
                  <DependencyRow key={String(d.id)} dep={d} onResolved={load} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// A single dependency row: title + description + kind pill, with kind-appropriate
// fulfillment UI inline.
export const DependencyRow: React.FC<{
  dep: Dependency;
  onResolved: () => void;
  // Optional: called when the row is dismissed/deleted (cleared without an
  // answer). Defaults to onResolved so the list reloads either way.
  onDismissed?: () => void;
  compact?: boolean;
}> = ({ dep, onResolved, onDismissed, compact }) => {
  const { theme } = useTheme();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiDraft, setAiDraft] = useState<string | null>(null);
  // A brief "✓ {agent}'s AI answered & cleared this" note shown right before the row clears, so
  // the 'handled' click never looks dead.
  const [aiHandled, setAiHandled] = useState<string | null>(null);

  const cleared = onDismissed || onResolved;

  const doResolve = async (resolveValue?: string) => {
    setSaving(true);
    setError(null);
    try {
      await resolveDependency(dep.id, resolveValue);
      setValue('');
      onResolved();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to resolve. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Dismiss: clear this ask out of the lane without answering it.
  const doDismiss = async () => {
    setSaving(true);
    setError(null);
    try {
      await dismissDependency(dep.id);
      cleared();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to dismiss. Please try again.');
      setSaving(false);
    }
  };

  // Hard delete: remove the ask entirely.
  const doDelete = async () => {
    setSaving(true);
    setError(null);
    try {
      await deleteDependency(dep.id);
      cleared();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete. Please try again.');
      setSaving(false);
    }
  };

  // "Let my AI handle it": route to the owner's AI rep. If it answers in-pattern
  // the ask clears; if it needs the human it shows the rep's proposed answer.
  const doHandleByAi = async () => {
    setAiBusy(true);
    setError(null);
    setAiDraft(null);
    setAiHandled(null);
    try {
      const res = await handleDependencyByAi(dep.id);
      if (res.outcome === 'handled') {
        // Show a brief success note, THEN clear it from the lane (never a silent disappear).
        setAiHandled(
          res.agent
            ? `✓ ${res.agent.charAt(0).toUpperCase() + res.agent.slice(1)}'s AI answered & cleared this`
            : res.message || '✓ Your AI rep answered & cleared this'
        );
        setAiBusy(false);
        window.setTimeout(() => cleared(), 1400);
        return;
      }
      setAiDraft(res.draft || 'Your AI rep drafted a proposed answer for your review.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Your AI rep could not handle this right now.');
    } finally {
      setAiBusy(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    flex: '1 1 240px',
    minWidth: 0,
    boxSizing: 'border-box',
    padding: '8px 10px',
    borderRadius: '6px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.background,
    color: theme.text,
    fontSize: '13px',
  };

  const btn = (bg: string): React.CSSProperties => ({
    padding: '8px 18px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: bg,
    color: '#fff',
    cursor: saving ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  });

  const envKey: string | undefined = dep.payload && dep.payload.env_key;

  return (
    <div
      style={{
        padding: '14px',
        backgroundColor: theme.background,
        border: `1px solid ${theme.warning}`,
        borderRadius: '8px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ color: theme.text, fontSize: '14px', fontWeight: 600 }}>
            {dep.title}
            {envKey && (
              <code
                style={{
                  marginLeft: '8px',
                  color: theme.textSecondary,
                  fontSize: '12px',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                }}
              >
                {envKey}
              </code>
            )}
          </div>
          {dep.description && (
            <div style={{ color: theme.textSecondary, fontSize: '13px', marginTop: '4px' }}>
              {dep.description}
            </div>
          )}
          {dep.source && (
            <div style={{ color: theme.textSecondary, fontSize: '12px', marginTop: '4px', opacity: 0.8 }}>
              from {dep.source}
            </div>
          )}
        </div>
        <span
          style={{
            padding: '4px 12px',
            borderRadius: '999px',
            backgroundColor: theme.warning + '22',
            color: theme.warning,
            fontSize: '12px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          {KIND_LABEL[dep.kind] || dep.kind}
        </span>
      </div>

      <div style={{ marginTop: '12px' }}>
        {dep.kind === 'api_key' && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="password"
              value={value}
              autoComplete="off"
              disabled={saving}
              placeholder={`Paste ${dep.title.replace(/^Provide /, '')}…`}
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && value.trim()) doResolve(value.trim());
              }}
              style={{ ...inputStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
              aria-label={`Provide ${dep.title}`}
            />
            <button
              type="button"
              onClick={() => value.trim() && doResolve(value.trim())}
              disabled={saving || !value.trim()}
              style={btn(saving || !value.trim() ? theme.border : theme.primary)}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}

        {dep.kind === 'approval' && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => doResolve('Approved.')}
              disabled={saving}
              style={btn(saving ? theme.border : theme.success)}
            >
              {saving ? 'Saving…' : 'Approve'}
            </button>
            <button
              type="button"
              onClick={() => doResolve('Declined.')}
              disabled={saving}
              style={btn(saving ? theme.border : '#ef4444')}
            >
              Decline
            </button>
          </div>
        )}

        {dep.kind === 'question' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <textarea
              value={value}
              disabled={saving}
              rows={compact ? 2 : 3}
              placeholder="Type your answer…"
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
              }}
              style={{ ...inputStyle, flex: 'none', width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
              aria-label={`Answer: ${dep.title}`}
            />
            <div>
              <button
                type="button"
                onClick={() => value.trim() && doResolve(value.trim())}
                disabled={saving || !value.trim()}
                style={btn(saving || !value.trim() ? theme.border : theme.primary)}
              >
                {saving ? 'Submitting…' : 'Submit answer'}
              </button>
            </div>
          </div>
        )}

        {dep.kind === 'task' && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              value={value}
              disabled={saving}
              placeholder="Optional note (what you did)…"
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') doResolve(value.trim() || undefined);
              }}
              style={inputStyle}
              aria-label={`Done note for ${dep.title}`}
            />
            <button
              type="button"
              onClick={() => doResolve(value.trim() || undefined)}
              disabled={saving}
              style={btn(saving ? theme.border : theme.success)}
            >
              {saving ? 'Saving…' : 'Mark done'}
            </button>
          </div>
        )}

        {(dep.kind === 'info' ||
          dep.kind === 'access' ||
          dep.kind === 'decision' ||
          dep.kind === 'other') && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              value={value}
              disabled={saving}
              placeholder="Resolution / note…"
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && value.trim()) doResolve(value.trim());
              }}
              style={inputStyle}
              aria-label={`Resolve ${dep.title}`}
            />
            <button
              type="button"
              onClick={() => value.trim() && doResolve(value.trim())}
              disabled={saving || !value.trim()}
              style={btn(saving || !value.trim() ? theme.border : theme.primary)}
            >
              {saving ? 'Saving…' : 'Resolve'}
            </button>
          </div>
        )}

        {/* Secondary actions: let the AI rep handle it, dismiss, or delete. The
            api_key kind is excluded from AI-handle (a secret needs the human). */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            alignItems: 'center',
            marginTop: '10px',
          }}
        >
          {dep.kind !== 'api_key' && !dep.synthetic && (
            <button
              type="button"
              onClick={doHandleByAi}
              disabled={aiBusy || saving}
              title="Route this to your AI rep to handle or draft an answer"
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme.primary}`,
                backgroundColor: 'transparent',
                color: theme.primary,
                cursor: aiBusy || saving ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              {aiBusy ? 'AI working…' : '🤖 Let my AI handle'}
            </button>
          )}
          {!dep.synthetic && (
            <>
              <button
                type="button"
                onClick={doDismiss}
                disabled={saving || aiBusy}
                title="Dismiss — clear this without answering"
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: 'transparent',
                  color: theme.textSecondary,
                  cursor: saving || aiBusy ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                ✕ Dismiss
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Delete this ask permanently? This cannot be undone.')) doDelete();
                }}
                disabled={saving || aiBusy}
                title="Delete this ask permanently"
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: 'transparent',
                  color: theme.textSecondary,
                  cursor: saving || aiBusy ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                Delete
              </button>
            </>
          )}
        </div>

        {aiHandled && (
          <div
            style={{
              marginTop: '10px',
              padding: '8px 12px',
              backgroundColor: theme.success + '12',
              border: `1px solid ${theme.success}`,
              borderRadius: '6px',
              color: theme.success,
              fontSize: '13px',
              fontWeight: 700,
            }}
          >
            {aiHandled}
          </div>
        )}

        {aiDraft && (
          <div
            style={{
              marginTop: '10px',
              padding: '10px 12px',
              backgroundColor: theme.primary + '12',
              border: `1px solid ${theme.primary}`,
              borderRadius: '6px',
            }}
          >
            <div style={{ color: theme.primary, fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>
              Your AI rep's proposed answer (needs your review)
            </div>
            <div style={{ color: theme.text, fontSize: '13px', whiteSpace: 'pre-wrap' }}>{aiDraft}</div>
            <div style={{ marginTop: '8px' }}>
              <button
                type="button"
                onClick={() => doResolve(aiDraft)}
                disabled={saving}
                style={btn(saving ? theme.border : theme.success)}
              >
                {saving ? 'Submitting…' : 'Accept this answer'}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '8px' }}>{error}</div>
        )}
      </div>
    </div>
  );
};

export default Dependencies;
